import React, { useEffect, useMemo, useState } from 'react';
import { useLocale } from '../LocaleProvider';

function normalize(value) {
  return String(value || '').trim().toLocaleLowerCase('pt-BR').normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

export function filterPatients(patients = [], query = '') {
  const needle = normalize(query);
  if (!needle) return patients;
  return patients.filter((patient) => normalize(patient.display_name || patient.name).includes(needle));
}

function formatBirthDate(value, es = false) {
  if (!value) return es ? 'Fecha de nacimiento no informada' : 'Nascimento não informado';
  const [year, month, day] = String(value).slice(0, 10).split('-');
  return year && month && day ? `${day}/${month}/${year}` : (es ? 'Fecha de nacimiento no informada' : 'Nascimento não informado');
}

export default function PatientPicker({
  patients = [],
  value = '',
  onChange,
  id = 'management-patient',
  disabled = false,
}) {
  const { locale } = useLocale();
  const es = locale === 'es';
  const selected = patients.find((patient) => String(patient.id) === String(value));
  const [query, setQuery] = useState(selected?.display_name || '');
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (selected && !open) setQuery(selected.display_name || '');
    if (!value && !open) setQuery('');
  }, [selected?.display_name, value, open]);

  const matches = useMemo(() => filterPatients(patients, query).slice(0, 8), [patients, query]);

  function choose(patient) {
    onChange?.(String(patient.id));
    setQuery(patient.display_name || patient.name || '');
    setOpen(false);
  }

  function handleChange(event) {
    const nextQuery = event.target.value;
    setQuery(nextQuery);
    setOpen(true);
    if (selected && normalize(nextQuery) !== normalize(selected.display_name)) onChange?.('');
  }

  return (
    <div className="relative">
      <label htmlFor={id} className="block text-sm font-bold text-[var(--primary)]">
        {es ? 'Persona atendida' : 'Pessoa acompanhada'}
      </label>
      <input
        id={id}
        type="search"
        role="combobox"
        value={query}
        disabled={disabled}
        autoComplete="off"
        placeholder={es ? 'Buscar por nombre' : 'Pesquisar por nome'}
        aria-autocomplete="list"
        aria-controls={`${id}-options`}
        aria-expanded={open}
        aria-describedby={selected ? `${id}-selected` : undefined}
        onChange={handleChange}
        onFocus={() => setOpen(true)}
        onBlur={() => window.setTimeout(() => setOpen(false), 120)}
        className="input-field mt-2"
      />
      {selected && (
        <p id={`${id}-selected`} className="mt-1 text-xs text-[var(--text-muted)]">
          {es ? 'Seleccionada' : 'Selecionada'}: {selected.display_name} · {formatBirthDate(selected.birth_date, es)}
        </p>
      )}
      {open && (
        <div
          id={`${id}-options`}
          role="listbox"
          aria-label={es ? 'Personas encontradas' : 'Pessoas encontradas'}
          className="absolute z-20 mt-2 max-h-56 w-full overflow-auto rounded-xl border border-[var(--border)] bg-[var(--surface)] p-1 shadow-lg"
        >
          {matches.length ? matches.map((patient) => (
            <button
              key={patient.id}
              type="button"
              role="option"
              aria-selected={String(patient.id) === String(value)}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => choose(patient)}
              className="w-full rounded-lg px-3 py-2 text-left hover:bg-[var(--surface-soft)] focus:bg-[var(--surface-soft)] focus:outline-none"
            >
              <span className="block font-semibold text-[var(--primary)]">{patient.display_name || patient.name}</span>
              <span className="block text-xs text-[var(--text-muted)]">{formatBirthDate(patient.birth_date, es)}</span>
            </button>
          )) : (
            <p className="px-3 py-3 text-sm text-[var(--text-muted)]">{es ? 'No se encontraron personas.' : 'Nenhuma pessoa encontrada.'}</p>
          )}
        </div>
      )}
    </div>
  );
}
